import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router";
import { eq } from "../../../utils";

type IdGetter = (element: HTMLElement) => string;

type Midline = '30%' | 'middle';

/**
 * Returns all elements currently in view.
 */
function listElementsInView(selector: string): HTMLElement[] {
  const { innerHeight } = window;
  const elements = [...document.querySelectorAll(selector)] as HTMLElement[];

  return elements.filter(element => {
    const bounds = element.getBoundingClientRect();
    return (bounds.top > 0 && bounds.top < innerHeight) ||
      (bounds.bottom > 0 && bounds.bottom < innerHeight) ||
      (bounds.top <= 0 && bounds.bottom >= innerHeight)
  });
}

function getClosestElementInView(elements: HTMLElement[], midline: Midline): HTMLElement | null {
  if (!elements.length) {
    return null;
  }

  //  The element closest to this on the screen line wins.
  const middleLine = window.innerHeight * (midline === '30%' ? 0.3 : 0.5);

  const elementsAndDistance = elements.map(element => {
    const bounds = element.getBoundingClientRect();

    //  Some arbitrary padding to work around an iOS (versions without support for CSS gap) issue where
    //  the previous element's `bottom` is the same as the next element's `top` due to the lack of a gap,
    //  and thus the previous element is returned.
    const gaplessWorkaroundPadding = 1;

    return {
      element,
      distanceToMiddleLine: Math.min(
        Math.abs(bounds.top + gaplessWorkaroundPadding - middleLine),
        Math.abs(bounds.bottom - gaplessWorkaroundPadding - middleLine)
      )
    };
  });

  return elementsAndDistance.reduce((previous, element) => {
    if (element.distanceToMiddleLine < previous.distanceToMiddleLine) {
      return element;
    }

    return previous;
  }, elementsAndDistance[0]).element;
}

export type ElementsInView = {
  /**
   * All elements currently in view.
   */
  allElementsInView: HTMLElement[];

  /**
   * All elements which has been in view.
   */
  elementsInViewHistory: HTMLElement[];

  /**
   * ID of all elements which has been in view. You can use an `includes()` to check whether
   * a specific element has been in view, and for example run a React effect.
   * 
   * The ID is that which was returned from getId, so it might not neccessarily be the element's id.
   */
  elementsInViewIdHistory: string[];

  /**
   * ID of the closest element currently in view.
   */
  closestElementInViewId: string | null;
};

type UseElementsInViewOptions = {
  /**
   * Defaults to `element.id`.
   */
  getId?: IdGetter;

  /**
   * The vertical line where view focus is changed.
   * A higher-up line is better for smaller views.
   */
  midline?: Midline;
}

/**
 * A React hook which returns elements in view.
 */
export const useElementsInView = (selector: string, options?: UseElementsInViewOptions): ElementsInView => {
  const midline = options?.midline || 'middle';
  const getId = options?.getId || (element => element.id);
  const safeGetId: IdGetter = element => element ? getId(element) : null;

  const location = useLocation();
  const previousScrollY = useRef<number>(0);
  const [allElementsInView, setElementsInView] = useState<HTMLElement[]>(listElementsInView(selector));
  const [elementsInViewHistory, setElementsInViewHistory] = useState<{ [id: string]: HTMLElement }>({});
  const [closestElementInViewId, setClosestElementInViewId] = useState<string | null>(
    safeGetId(getClosestElementInView(allElementsInView, midline)));

  useEffect(() => {
    setElementsInViewHistory(history => {
      history = { ...history };

      allElementsInView.forEach(element => history[getId(element)] = element);
      return history;
    });
  }, [allElementsInView]);

  function update() {
    setElementsInView(elements => {
      const nextElements = listElementsInView(selector);
      const nextElementIds = nextElements.map(element => getId(element)).filter(id => id);

      const closestElementId = safeGetId(getClosestElementInView(nextElements, midline));
      //  Having the closest's elements ID as a state has several benefits:
      //  - We don't need to check the previous value to determine whether we really need to trigger a render update.
      //  - If we had an HTMLElement, we would risk a render update not happending at all on this update, if the same element's ID changed. 
      setClosestElementInViewId(closestElementId);

      if (eq(nextElementIds, elements.map(element => getId(element)))) {
        //  Do not trigger a state update.
        return elements;
      }

      return nextElements;
    });
  }

  useEffect(() => {
    const scrollListener = () => {
      //  Apply threshold for better performance.
      if (Math.abs(previousScrollY.current - window.scrollY) < 30) {
        return;
      }

      update();
      previousScrollY.current = window.scrollY;
    };

    const resizeListener = () => update();

    window.addEventListener('scroll', scrollListener);
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('scroll', scrollListener);
      window.removeEventListener('resize', resizeListener);
    }
  }, []);

  useEffect(() => {
    //  Wait a render cycle before updating.
    //  Keep in mind that this component is likely higher level than the elements themselves.
    const updateId = setTimeout(() => update(), 10);

    return () => { clearTimeout(updateId); }
  }, [location.pathname, location.hash]);

  const elementsInViewHistoryList = Object.values(elementsInViewHistory);

  return {
    closestElementInViewId,
    elementsInViewIdHistory: elementsInViewHistoryList.map(element => getId(element)),
    elementsInViewHistory: elementsInViewHistoryList,
    allElementsInView
  };
}